import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { toKebabCase } from '@klareospace/pharos-lib-utils';
import type { HandlingUnitType } from '@klareospace/pharos-lib-types';
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SideDrawer,
} from '@klareospace/pharos-lib-web-utils';

import { useHandlingUnitTypeForm } from '../hooks';

// ─── Types ────────────────────────────────────────────────────────────────────

interface HandlingUnitTypeDrawerProps {
  handlingUnitType: HandlingUnitType | null;
  onClose: () => void;
  open: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function HandlingUnitTypeDrawer({
  handlingUnitType,
  onClose,
  open,
}: HandlingUnitTypeDrawerProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');
  const isEditMode = handlingUnitType !== null;

  const { form, isPending, onSubmit } = useHandlingUnitTypeForm(handlingUnitType, onClose);

  const codeManuallyEdited = useRef(false);

  useEffect(() => {
    if (open && !isEditMode) {
      codeManuallyEdited.current = false;
    }
  }, [open, isEditMode]);

  const title = isEditMode ? t('handlingUnitTypes.drawer.editTitle') : t('handlingUnitTypes.drawer.title');
  const subtitle = isEditMode ? t('handlingUnitTypes.drawer.editSubtitle') : t('handlingUnitTypes.drawer.subtitle');

  function handleOpenChange(nextOpen: boolean): void {
    if (!nextOpen) {
      form.reset();
      onClose();
    }
  }

  function handleLabelChange(
    event: React.ChangeEvent<HTMLInputElement>,
    fieldOnChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  ): void {
    fieldOnChange(event);
    if (!isEditMode && !codeManuallyEdited.current) {
      form.setValue('code', toKebabCase(event.target.value), { shouldValidate: true });
    }
  }

  function handleCodeChange(
    event: React.ChangeEvent<HTMLInputElement>,
    fieldOnChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  ): void {
    codeManuallyEdited.current = true;
    fieldOnChange(event);
  }

  const footer = (
    <>
      <Button disabled={isPending} onClick={form.handleSubmit(onSubmit)} type="button">
        {isEditMode ? tCommon('actions.submitChanges') : tCommon('actions.create')}
      </Button>
      <Button onClick={(): void => handleOpenChange(false)} type="button" variant="outline">
        {tCommon('actions.cancel')}
      </Button>
    </>
  );

  return (
    <SideDrawer footer={footer} onOpenChange={handleOpenChange} open={open} subtitle={subtitle} title={title}>
      <Form {...form}>
        <form className="space-y-4">
          <FormField
            control={form.control}
            name="label"
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>
                  {tCommon('fields.label')} <span>*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onChange={(e): void => handleLabelChange(e, field.onChange)}
                    placeholder={t('handlingUnitTypes.drawer.fields.labelPlaceholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="code"
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>
                  {tCommon('fields.code')} <span>*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className={
                      isEditMode
                        ? 'cursor-not-allowed opacity-60 font-mono text-xs bg-muted text-muted-foreground'
                        : 'font-mono text-xs'
                    }
                    disabled={isEditMode}
                    onChange={(e): void => handleCodeChange(e, field.onChange)}
                    placeholder={t('handlingUnitTypes.drawer.fields.codePlaceholder')}
                  />
                </FormControl>
                <FormDescription>
                  {isEditMode
                    ? t('handlingUnitTypes.drawer.fields.codeHelpReadOnly')
                    : t('handlingUnitTypes.drawer.fields.codeHelp')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="weightValue"
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>
                  {tCommon('fields.weight')} <span>*</span>
                </FormLabel>
                <div className="flex gap-2">
                  <FormControl>
                    <Input {...field} className="flex-1" placeholder="0" min={0} step={1} type="number" />
                  </FormControl>
                  <FormField
                    control={form.control}
                    name="weightUnit"
                    render={({ field: unitField }): React.JSX.Element => (
                      <Select onValueChange={unitField.onChange} value={unitField.value}>
                        <SelectTrigger aria-label={tCommon('fields.weightUnit')} className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="kg">{tCommon('units.kg')}</SelectItem>
                          <SelectItem value="lb">{tCommon('units.lb')}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="volumeValue"
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>
                  {tCommon('fields.volume')} <span>*</span>
                </FormLabel>
                <div className="flex gap-2">
                  <FormControl>
                    <Input {...field} className="flex-1" placeholder="0" min={0} step={1} type="number" />
                  </FormControl>
                  <FormField
                    control={form.control}
                    name="volumeUnit"
                    render={({ field: unitField }): React.JSX.Element => (
                      <Select onValueChange={unitField.onChange} value={unitField.value}>
                        <SelectTrigger aria-label={tCommon('fields.volumeUnit')} className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="L">{tCommon('units.L')}</SelectItem>
                          <SelectItem value="m3">{tCommon('units.m3')}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </SideDrawer>
  );
}
